import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  IoClose,
  IoPeople,
  IoPersonAdd,
  IoTrashOutline,
  IoWarningOutline,
} from 'react-icons/io5';

import {
  fetchProjectMembers,
  addProjectMember,
  updateProjectMember,
  removeProjectMember,
  fetchMembershipTypes,
  fetchProjectScope,
} from '../../services/api';

// One role per member. The member type decides both what they can see and
// how mail is ranked for them (BA and Consultant are Members with their own
// mail lens). Show the role, not the code, in this order.
const ROLE_LABELS = {
  OWNER: 'Owner',
  PM: 'Manager (PM)',
  PMO: 'Manager (PMO)',
  LEAD: 'Lead',
  SCRUM_MASTER: 'Lead (Scrum Master)',
  BA: 'Member (BA)',
  CONSULTANT: 'Member (Consultant)',
  MEMBER: 'Member',
  CLIENT: 'Guest',
};
const roleLabel = (type) => ROLE_LABELS[type] || type;
const ROLE_ORDER = Object.keys(ROLE_LABELS);
const byRoleOrder = (a, b) =>
  (ROLE_ORDER.indexOf(a) + 1 || 99) - (ROLE_ORDER.indexOf(b) + 1 || 99);

/**
 * Manage who is on a project.
 *
 * A project is the delivery team: PM, Scrum Master, developers and QA are all
 * members of the same project. A project can hold several teams. What a
 * member sees follows their role: the owner and a manager with no team see
 * everything, a lead or a manager on a team sees that team's items, a member
 * or guest sees their own.
 *
 * Only someone who may change the roster gets the controls; everyone else
 * sees the same list read-only.
 *
 * Members are added by email and must already have an account — there is no
 * invite flow, and there is deliberately no directory to browse.
 */
export const ProjectMembersModal = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  userIdToken,
  currentUserId,
}) => {
  const [members, setMembers] = useState([]);
  const [membershipTypes, setMembershipTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newType, setNewType] = useState('MEMBER');
  const [newTeam, setNewTeam] = useState('');
  const [canManage, setCanManage] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [membersRes, typesRes, scopeRes] = await Promise.all([
        fetchProjectMembers(userIdToken, projectId),
        fetchMembershipTypes(userIdToken),
        // Read-only on failure: the API refuses the change anyway.
        fetchProjectScope(userIdToken, projectId).catch(() => ({ data: {} })),
      ]);
      setCanManage(Boolean(scopeRes.data?.can_manage_members));
      setMembers(membersRes.data || []);
      setMembershipTypes([...(typesRes.data || [])].sort(byRoleOrder));
    } catch (error) {
      toast.error(`Failed to load members: ${error.message || error}`);
    } finally {
      setIsLoading(false);
    }
  }, [userIdToken, projectId]);

  useEffect(() => {
    if (isOpen && projectId) loadData();
  }, [isOpen, projectId, loadData]);

  if (!isOpen) return null;

  const ownerCount = members.filter(
    (m) => m.membership_type === 'OWNER',
  ).length;
  const teams = [...new Set(members.map((m) => m.team).filter(Boolean))].sort();

  const handleAdd = async (e) => {
    e.preventDefault();
    const email = newEmail.trim();
    if (!email) return;

    setIsAdding(true);
    try {
      await addProjectMember(userIdToken, projectId, {
        email,
        membershipType: newType,
        team: newTeam.trim() || null,
      });
      toast.success(`${email} added to ${projectName || 'project'}`);
      setNewEmail('');
      setNewType('MEMBER');
      setNewTeam('');
      await loadData();
    } catch (error) {
      const message = error.message || error;
      // The backend distinguishes these; surfacing the difference is what
      // makes a typo actionable rather than mysterious.
      if (String(message).includes('not found')) {
        toast.error(
          `No account for ${email}. They must be invited to the platform first.`,
        );
      } else {
        toast.error(`Could not add member: ${message}`);
      }
    } finally {
      setIsAdding(false);
    }
  };

  const handleTypeChange = async (member, membershipType) => {
    const previous = members;
    // Optimistic — a role dropdown that lags feels broken.
    setMembers((current) =>
      current.map((m) =>
        m.user_id === member.user_id
          ? { ...m, membership_type: membershipType }
          : m,
      ),
    );
    try {
      await updateProjectMember(userIdToken, projectId, member.user_id, {
        membership_type: membershipType,
      });
      toast.success(
        `${member.name || member.email} is now ${roleLabel(membershipType)}`,
      );
    } catch (error) {
      setMembers(previous);
      toast.error(`Could not update: ${error.message || error}`);
    }
  };

  const handleTeamChange = async (member, value) => {
    const team = value.trim();
    if (team === (member.team || '')) return;
    const previous = members;
    setMembers((current) =>
      current.map((m) =>
        m.user_id === member.user_id ? { ...m, team: team || null } : m,
      ),
    );
    try {
      // An empty string takes the member out of their team.
      await updateProjectMember(userIdToken, projectId, member.user_id, {
        team,
      });
      toast.success(
        team
          ? `${member.name || member.email} is on team ${team}`
          : `${member.name || member.email} is on no team`,
      );
    } catch (error) {
      setMembers(previous);
      toast.error(`Could not update: ${error.message || error}`);
    }
  };

  const handleRemove = async (member) => {
    try {
      await removeProjectMember(userIdToken, projectId, member.user_id);
      toast.success(`${member.name || member.email} removed`);
      setPendingRemoval(null);
      await loadData();
    } catch (error) {
      toast.error(`Could not remove: ${error.message || error}`);
      setPendingRemoval(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-black bg-opacity-60 p-4 backdrop-blur-sm">
      <div className="animate-fade-in-up flex max-h-[90vh] w-full max-w-3xl flex-col rounded-xl border border-zinc-700 bg-zinc-900 shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between rounded-t-xl border-b border-zinc-700 bg-zinc-800/40 p-5">
          <h3 className="flex items-center gap-2 text-xl font-bold text-zinc-100">
            <IoPeople className="text-[#5652CF]" />
            Project Team
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 transition-colors hover:bg-zinc-800"
          >
            <IoClose className="h-6 w-6 text-zinc-400" />
          </button>
        </div>

        {/* Body */}
        <div className="custom-scrollbar flex-1 overflow-y-auto p-6">
          <p className="mb-5 text-sm text-zinc-400">
            People on{' '}
            <strong className="text-zinc-100">
              {projectName || 'this project'}
            </strong>
            . The owner and a manager with no team see everything. A lead, or a
            manager on a team, sees that team's items. A member or guest sees
            their own items.
          </p>
          {canManage && (
            <p className="-mt-3 mb-5 text-xs text-zinc-400">
              For each person you can set the team and the role in this project.
              The role also decides how mail is ranked for them.
            </p>
          )}
          <datalist id="project-team-names">
            {teams.map((t) => (
              <option key={t} value={t} />
            ))}
          </datalist>

          {/* Add member */}
          {canManage && (
            <form
              onSubmit={handleAdd}
              className="mb-6 rounded-lg border border-zinc-700 bg-zinc-800/40 p-4"
            >
              <label
                htmlFor="new-member-email"
                className="mb-2 block text-sm font-semibold text-zinc-100"
              >
                Add a teammate
              </label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <input
                  id="new-member-email"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="their@email.com"
                  className="flex-1 rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#5652CF]"
                  disabled={isAdding}
                />
                <select
                  value={newType}
                  onChange={(e) => setNewType(e.target.value)}
                  className="rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#5652CF]"
                  disabled={isAdding}
                >
                  {membershipTypes.map((t) => (
                    <option key={t} value={t}>
                      {roleLabel(t)}
                    </option>
                  ))}
                </select>
                <input
                  aria-label="Team for the new teammate"
                  list="project-team-names"
                  value={newTeam}
                  onChange={(e) => setNewTeam(e.target.value)}
                  placeholder="Team (optional)"
                  maxLength={60}
                  className="w-full rounded-lg border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#5652CF] sm:w-36"
                  disabled={isAdding}
                />
                <button
                  type="submit"
                  disabled={isAdding || !newEmail.trim()}
                  className="flex items-center justify-center gap-2 rounded-lg bg-[#5652CF] px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <IoPersonAdd className="h-4 w-4" />
                  {isAdding ? 'Adding…' : 'Add'}
                </button>
              </div>
              <p className="mt-2 text-xs text-zinc-400">
                They need an existing account. Ask an admin to create one first
                if they do not have access yet.
              </p>
            </form>
          )}

          {/* Member list */}
          {isLoading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#5652CF] border-t-transparent"></div>
            </div>
          ) : members.length === 0 ? (
            <p className="rounded-lg bg-zinc-800 p-4 text-center text-sm text-zinc-400">
              No members yet.
            </p>
          ) : (
            <div className="space-y-2">
              {members.map((member) => {
                const isSelf = member.user_id === currentUserId;
                const isLastOwner =
                  member.membership_type === 'OWNER' && ownerCount <= 1;

                return (
                  <div
                    key={member.user_id}
                    className="flex flex-col gap-3 rounded-lg border border-zinc-700 bg-zinc-800/30 p-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-zinc-100">
                        {member.name || member.email || member.user_id}
                        {isSelf && (
                          <span className="ml-2 text-xs font-normal text-zinc-400">
                            (you)
                          </span>
                        )}
                      </p>
                      {member.email && (
                        <p className="truncate text-xs text-zinc-400">
                          {member.email}
                        </p>
                      )}
                    </div>

                    {!canManage ? (
                      <p className="shrink-0 text-xs text-zinc-400">
                        {roleLabel(member.membership_type)}
                        {member.team ? ` · ${member.team}` : ''}
                      </p>
                    ) : (
                      <div className="flex shrink-0 items-end gap-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                            Team
                          </span>
                          <input
                            key={`${member.user_id}-${member.team || ''}`}
                            aria-label={`Team for ${member.name || member.email || member.user_id}`}
                            list="project-team-names"
                            defaultValue={member.team || ''}
                            onBlur={(e) =>
                              handleTeamChange(member, e.target.value)
                            }
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') e.currentTarget.blur();
                            }}
                            placeholder="No team"
                            maxLength={60}
                            title="Team inside this project. A lead sees the items of the members on their team."
                            className="w-32 rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-[#5652CF]"
                          />
                        </div>
                        <div className="flex flex-col gap-0.5">
                          <span className="text-[10px] uppercase tracking-wide text-zinc-500">
                            Role
                          </span>
                          <select
                            aria-label={`Role for ${member.name || member.email || member.user_id}`}
                            value={member.membership_type}
                            onChange={(e) =>
                              handleTypeChange(member, e.target.value)
                            }
                            disabled={isLastOwner}
                            title={
                              isLastOwner
                                ? 'Promote another member to owner first'
                                : undefined
                            }
                            className="rounded-lg border border-zinc-700 bg-zinc-900 px-2 py-1.5 text-xs text-zinc-100 outline-none focus:border-[#5652CF] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {membershipTypes.map((t) => (
                              <option key={t} value={t}>
                                {roleLabel(t)}
                              </option>
                            ))}
                          </select>
                        </div>

                        <button
                          onClick={() => setPendingRemoval(member)}
                          disabled={isLastOwner}
                          title={
                            isLastOwner
                              ? 'A project must keep at least one owner'
                              : 'Remove from project'
                          }
                          className="rounded-lg p-2 text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:opacity-30"
                        >
                          <IoTrashOutline className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Removal confirmation — removing someone revokes their access to
            every piece of project information, so it asks first. */}
        {pendingRemoval && (
          <div className="shrink-0 border-t border-zinc-700 bg-zinc-800/50 p-5">
            <div className="mb-3 flex items-start gap-2">
              <IoWarningOutline className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <p className="text-sm text-zinc-100">
                Remove{' '}
                <strong>
                  {pendingRemoval.name ||
                    pendingRemoval.email ||
                    pendingRemoval.user_id}
                </strong>{' '}
                from this project? They will immediately lose access to all of
                its information.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setPendingRemoval(null)}
                className="rounded-lg bg-zinc-800 px-4 py-2 text-sm font-semibold text-zinc-100 transition-opacity hover:opacity-80"
              >
                Cancel
              </button>
              <button
                onClick={() => handleRemove(pendingRemoval)}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectMembersModal;
